import { useEffect } from 'react';
import { tabbable } from 'tabbable';
import { KEYBOARD } from '../constants/index.js';

/**
 * This is HEAVILY inpsired by the Reach UI Popover
 * https://github.com/reach/reach-ui/blob/master/packages/popover
 */
function useSimulateTabNavigationForReactTree(options) {
  var triggerRef = options.triggerRef,
    attachmentRef = options.attachmentRef;
  useEffect(function () {
    function getElementAfterTrigger() {
      if (triggerRef.current === null) {
        return undefined;
      }
      var elements = tabbable(document.body);
      var targetIndex = elements.indexOf(triggerRef.current);
      return elements[targetIndex + 1];
    }
    function shiftTabbedFromElementAfterTrigger(event) {
      if (event.shiftKey) {
        return event.target === getElementAfterTrigger();
      }
      return false;
    }
    function focusLastTabbableInPopover(event) {
      if (attachmentRef.current === null) {
        return;
      }
      var elements = tabbable(attachmentRef.current);
      var last = elements[elements.length - 1];
      if (last) {
        event.preventDefault();
        last.focus();
      }
    }
    function shiftTabbedOutOfPopover(event) {
      if (attachmentRef.current === null) {
        return false;
      }
      var elements = tabbable(attachmentRef.current);
      return elements.length === 0 ? false : event.target === elements[0];
    }
    function focusTriggerRef(event) {
      event.preventDefault();
      if (triggerRef.current !== null) {
        triggerRef.current.focus();
      }
    }
    function shiftTabbedToBrowserChrome(event) {
      // we're assuming the popover will never contain the first tabbable
      // element, and it better not, because the trigger needs to be tabbable!
      return event.target === tabbable(document.body)[0];
    }
    var restoreTabIndexTuplés = [];
    function enableTabbablesInPopover() {
      document.removeEventListener('focusin', enableTabbablesInPopover);
      restoreTabIndexTuplés.forEach(function (_ref) {
        var element = _ref[0],
          tabIndex = _ref[1];
        // eslint-disable-next-line no-param-reassign
        element.tabIndex = tabIndex;
      });
    }
    function disableTabbablesInPopover() {
      if (attachmentRef.current === null) {
        return;
      }
      var elements = tabbable(attachmentRef.current);
      elements.forEach(function (element) {
        restoreTabIndexTuplés.push([element, element.tabIndex]);
        // eslint-disable-next-line no-param-reassign
        element.tabIndex = -1;
      });
      document.addEventListener('focusin', enableTabbablesInPopover);
    }
    function tabbedFromTriggerToPopover() {
      return triggerRef.current === document.activeElement;
    }
    function focusFirstPopoverTabbable(event) {
      if (attachmentRef.current === null) {
        return;
      }
      var elements = tabbable(attachmentRef.current);
      if (elements[0]) {
        event.preventDefault();
        elements[0].focus();
      }
    }
    function tabbedOutOfPopover() {
      if (attachmentRef.current === null) {
        return false;
      }
      var inPopover = attachmentRef.current.contains(document.activeElement);
      if (inPopover) {
        var elements = tabbable(attachmentRef.current);
        return elements[elements.length - 1] === document.activeElement;
      }
      return false;
    }
    function focusTabbableAfterTrigger(event) {
      var elementAfterTrigger = getElementAfterTrigger();
      if (elementAfterTrigger) {
        event.preventDefault();
        elementAfterTrigger.focus();
      }
    }
    function tabbedToBrowserChrome(event) {
      if (attachmentRef.current === null) {
        return false;
      }
      var elements = tabbable(document.body).filter(function (element) {
        var _a;
        return !((_a = attachmentRef.current) === null || _a === void 0 ? void 0 : _a.contains(element));
      });
      return event.target === elements[elements.length - 1];
    }
    function handleKeyDown(event) {
      if (event.key !== KEYBOARD.TAB) {
        return;
      }
      if (attachmentRef.current === null || tabbable(attachmentRef.current).length === 0) {
        return;
      }
      if (event.shiftKey) {
        if (shiftTabbedFromElementAfterTrigger(event)) {
          focusLastTabbableInPopover(event);
        } else if (shiftTabbedOutOfPopover(event)) {
          focusTriggerRef(event);
        } else if (shiftTabbedToBrowserChrome(event)) {
          disableTabbablesInPopover();
        }
      } else if (tabbedFromTriggerToPopover()) {
        focusFirstPopoverTabbable(event);
      } else if (tabbedOutOfPopover()) {
        focusTabbableAfterTrigger(event);
      } else if (tabbedToBrowserChrome(event)) {
        disableTabbablesInPopover();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [triggerRef, attachmentRef]);
}

export { useSimulateTabNavigationForReactTree };
